import { useEffect, useState } from 'react'
import { useGroupStore } from '../store'
import { useAuthStore } from '../../auth/store'
import { ResponsiveLayout } from '../../../shared/components/layout'
import { StatusCard, Avatar, Badge } from '../../../shared/components/widgets'
import { EmptyState } from '../../../shared/components/EmptyState'

export function GroupPage() {
  const { user } = useAuthStore()
  const {
    group,
    members,
    pendingInvitations,
    isLoading,
    error,
    fetchGroup,
    fetchMembers,
    fetchPendingInvitations,
    createInvitation,
    cancelInvitation,
    updateMemberRole,
    removeMember,
    clearError,
  } = useGroupStore()

  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'admin' | 'cuidador'>('cuidador')
  const [devInviteLink, setDevInviteLink] = useState<string | null>(null)

  const isAdmin = user?.role === 'admin'

  useEffect(() => {
    fetchGroup()
  }, [fetchGroup])

  useEffect(() => {
    if (group) {
      fetchMembers()
      if (isAdmin) {
        fetchPendingInvitations()
      }
    }
  }, [group, fetchMembers, fetchPendingInvitations, isAdmin])

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const result = await createInvitation(inviteEmail, inviteRole)
      if (result.devInviteLink) {
        setDevInviteLink(result.devInviteLink)
      }
      setInviteEmail('')
      setShowInviteForm(false)
    } catch {
      // Error handled in store
    }
  }

  const handleRemoveMember = async (memberId: string) => {
    if (!confirm('¿Seguro que quieres eliminar este miembro?')) return
    try {
      await removeMember(memberId)
    } catch {
      // Error handled in store
    }
  }

  const handleChangeRole = async (memberId: string, newRole: 'admin' | 'cuidador') => {
    try {
      await updateMemberRole(memberId, newRole)
    } catch {
      // Error handled in store
    }
  }

  if (isLoading && !group) {
    return (
      <ResponsiveLayout>
        <div className="page">
          <div className="loading-container">
            <div className="spinner" />
          </div>
        </div>
      </ResponsiveLayout>
    )
  }

  return (
    <ResponsiveLayout>
      <div className="page">
        <div className="page-header">
          <h1>Grupo de Cuidado</h1>
        </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={clearError} className="alert-dismiss">&times;</button>
        </div>
      )}

      {devInviteLink && (
        <div className="alert alert-info">
          <p><strong>Link de invitación (desarrollo):</strong></p>
          <a href={devInviteLink} target="_blank" rel="noopener noreferrer">{devInviteLink}</a>
          <button onClick={() => setDevInviteLink(null)} className="alert-dismiss">&times;</button>
        </div>
      )}

      {group && (
        <StatusCard status="neutral" className="group-info-card">
          <StatusCard.Header>
            <StatusCard.Title>{group.nombre}</StatusCard.Title>
          </StatusCard.Header>
          {group.descripcion && (
            <StatusCard.Content collapsible={false}>
              <p>{group.descripcion}</p>
            </StatusCard.Content>
          )}
        </StatusCard>
      )}

      <section className="section">
        <div className="section-header">
          <h2>Miembros ({members.length})</h2>
          {isAdmin && (
            <button onClick={() => setShowInviteForm(!showInviteForm)} className="btn btn-primary btn-sm">
              Invitar
            </button>
          )}
        </div>

        {showInviteForm && isAdmin && (
          <StatusCard status="info" className="invite-form-card">
            <StatusCard.Content collapsible={false}>
              <form onSubmit={handleInvite} className="invite-form">
                <div className="form-row">
                  <div className="form-group flex-grow">
                    <label htmlFor="inviteEmail">Email</label>
                    <input
                      type="email"
                      id="inviteEmail"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      required
                      className="form-control"
                      placeholder="correo@ejemplo.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="inviteRole">Rol</label>
                    <select
                      id="inviteRole"
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as 'admin' | 'cuidador')}
                      className="form-control"
                    >
                      <option value="cuidador">Cuidador</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" onClick={() => setShowInviteForm(false)} className="btn btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isLoading}>
                    Enviar invitación
                  </button>
                </div>
              </form>
            </StatusCard.Content>
          </StatusCard>
        )}

        {members.length === 0 ? (
          <EmptyState title="Sin miembros" description="No hay miembros en este grupo." />
        ) : (
          <div className="members-list">
            {members.map((member) => (
              <StatusCard key={member.id} status="neutral" className="member-card">
                <StatusCard.Header>
                  <Avatar
                    fallback={member.nombre.charAt(0).toUpperCase()}
                    size="sm"
                  />
                  <div className="member-details">
                    <StatusCard.Title>{member.nombre}</StatusCard.Title>
                    <StatusCard.Subtitle>{member.email}</StatusCard.Subtitle>
                  </div>
                  <Badge variant="status" status={member.role === 'admin' ? 'info' : 'neutral'}>
                    {member.role === 'admin' ? 'Admin' : 'Cuidador'}
                  </Badge>
                </StatusCard.Header>
                {isAdmin && member.id !== user?.id && (
                  <StatusCard.Content collapsible={false}>
                    <div className="member-actions">
                      <select
                        value={member.role}
                        onChange={(e) => handleChangeRole(member.id, e.target.value as 'admin' | 'cuidador')}
                        className="form-control form-control-sm"
                      >
                        <option value="cuidador">Cuidador</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button onClick={() => handleRemoveMember(member.id)} className="btn btn-danger btn-sm">
                        Eliminar
                      </button>
                    </div>
                  </StatusCard.Content>
                )}
              </StatusCard>
            ))}
          </div>
        )}
      </section>

      {isAdmin && pendingInvitations.length > 0 && (
        <section className="section">
          <h2>Invitaciones pendientes ({pendingInvitations.length})</h2>
          <div className="invitations-list">
            {pendingInvitations.map((inv) => (
              <StatusCard key={inv.id} status="warning" className="invitation-card">
                <StatusCard.Header>
                  <div className="invitation-info">
                    <StatusCard.Title>{inv.email}</StatusCard.Title>
                    <StatusCard.Subtitle>
                      Expira: {new Date(inv.expiresAt).toLocaleDateString()}
                    </StatusCard.Subtitle>
                  </div>
                  <Badge variant="status" status="warning">{inv.role}</Badge>
                  <button onClick={() => cancelInvitation(inv.id)} className="btn btn-ghost btn-sm">
                    Cancelar
                  </button>
                </StatusCard.Header>
              </StatusCard>
            ))}
          </div>
        </section>
      )}
      </div>
    </ResponsiveLayout>
  )
}
